// ---- Rig contract ----
// THE named-joint vocabulary shared by every jointed humanoid: the hand-coded
// player rig (src/player.ts) and the humanoid archetype builder
// (src/model/humanoid.ts) all tag their pivot groups with
// these names, so ONE clip/stance/hold system (src/anim/{clips,pose}.ts) and ONE
// gait animator (src/model/gait.ts) drive all of them. A rig is SPARSE by
// design — a simple humanoid without knee pivots just lacks those keys, and
// every animator write no-ops on a missing joint (same contract gait.ts always
// had). Joint names double as the clip CHANNEL names (content/anim/clips.json),
// so this list is the single source both JOINT_CHANNELS (src/anim/clips.ts) and
// the anim-clips schema derive from.
//
// Type-only THREE import — this module is pure data + traversal, safe everywhere
// (client, headless server, codegen scripts).

import type * as THREE from "three";

/** Joint pivots a humanoid rig may publish (tagged `userData.limb`). Order is
 *  cosmetic. All optional per-rig — see the sparse-rig contract above. */
export const RIGS = {
  humanoid: {
    joints: [
      "hipL", "hipR", "kneeL", "kneeR",
      "shoulderL", "shoulderR", "elbowL", "elbowR",
      "torso", "head",
    ],
    /** Empty groups tagged `userData.socket` — attachment points, not joints.
     *  handMain = the fist on the shoulderL/elbowL chain (the jab arm);
     *  handOff = the other fist (posed onto the shaft for two-hand grips). */
    sockets: ["handMain", "handOff"],
  },
} as const;

export type HumanoidJoint = (typeof RIGS.humanoid.joints)[number];

/** A collected rig: joint + socket groups keyed by name. Sparse — index with
 *  optional chaining, or via the typed aliases below when the full set is known
 *  to exist (the player rig publishes everything). */
export type RigHandle = Partial<Record<string, THREE.Group>>;

/** Collect (once per call) every tagged joint/socket group under `root`.
 *  Generalizes gait.ts's limbsOf + the old hand-assembled PlayerParts. */
export function collectRig(root: THREE.Object3D): RigHandle {
  const rig: RigHandle = {};
  root.traverse((c) => {
    const j = c.userData?.limb as string | undefined;
    const s = c.userData?.socket as string | undefined;
    if (j) rig[j] = c as THREE.Group;
    if (s) rig[s] = c as THREE.Group;
  });
  return rig;
}
